import React, { useEffect, useState } from 'react';
import { createOrder, deleteOrder, downloadInvoice, emailOrder, ensureReview, updateOrder } from './operations';
import { buildInvoicePdf, saveBlob } from './invoicePdf';
import Invoice from './Invoice';

const COOLDOWN_MS = 2 * 60 * 1000;
const cooldownKey = (id) => `ow-email-sent-${id}`;
const remainingSeconds = (id) => {
  const sent = Number(localStorage.getItem(cooldownKey(id)) || 0);
  return Math.max(0, Math.ceil((sent + COOLDOWN_MS - Date.now()) / 1000));
};
const whatsappUrl = (phone) => `https://wa.me/${String(phone || '').replace(/\D/g, '')}`;
const toLocalInput = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function WorkOrders({ customers, employees, orders, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [service, setService] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState(60);
  const [rate, setRate] = useState('');
  const [transport, setTransport] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState('');
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  function openNew() {
    setEditing(null);
    setCustomerId('');
    setService('');
    setDescription('');
    setScheduledAt('');
    setDuration(60);
    setRate('');
    setTransport('');
    setOpen(true);
  }

  function openEdit(order) {
    const items = Array.isArray(order.items) ? order.items : [];
    const transportItem = items.find((item) => item.description === 'Transport');
    const labourItem = items.find((item) => item.description !== 'Transport') || items[0];
    setEditing(order);
    setCustomerId(order.customer_id || '');
    setService(order.service || '');
    setDescription(order.description || '');
    setScheduledAt(toLocalInput(order.scheduled_at));
    setDuration(order.duration_minutes || 60);
    setRate(String(labourItem?.rate ?? order.amount ?? ''));
    setTransport(transportItem ? String(transportItem.rate) : '');
    setOpen(true);
  }

  async function sendEmail(id, knownOrder) {
    if (remainingSeconds(id) > 0) throw new Error('Please wait before emailing this customer again.');
    const order = knownOrder || orders.find((item) => item.id === id);
    const customer = customers.find((item) => item.id === order.customer_id);
    if (!customer?.email) throw new Error('This customer has no email. Send the invoice via WhatsApp instead.');
    localStorage.setItem(cooldownKey(id), String(Date.now()));
    try {
      const pdf = await buildInvoicePdf(order, customer);
      await emailOrder(id, pdf.base64);
    } catch (failure) {
      localStorage.removeItem(cooldownKey(id));
      throw failure;
    }
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const items = [{ name: service.trim(), description: 'Labour', quantity: 1, rate: Number(rate) }];
      if (Number(transport) > 0) items.push({ name: service.trim(), description: 'Transport', quantity: 1, rate: Number(transport) });
      const fields = {
        customer_id: customerId, service: service.trim(),
        description: description.trim(), scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        duration_minutes: Number(duration), items, amount: items.reduce((sum, item) => sum + item.rate, 0)
      };
      if (editing) {
        const updated = await updateOrder(editing.id, { ...fields, updated_at: new Date().toISOString() });
        setOpen(false);
        setEditing(null);
        setMessage('Work order updated. Use Download PDF or send the invoice again if needed.');
        if (selected?.id === updated.id) setSelected(updated);
        await onRefresh();
        return;
      }
      const order = await createOrder({
        ...fields,
        invoice_number: `OW-${Date.now()}-${crypto.randomUUID().slice(0, 6).toUpperCase()}`
      });
      await ensureReview(order);
      setOpen(false);
      await onRefresh();
      setSelected(order);
      const customer = customers.find((item) => item.id === order.customer_id);
      if (!customer?.email) {
        setMessage('Work order saved. Customer has no email, so no email was sent. Use the WhatsApp number on the order to send the invoice.');
      } else {
        try {
          await sendEmail(order.id, order);
          setMessage('Work order saved. Confirmation and invoice email sent.');
        } catch (failure) {
          setMessage(`Work order saved, but email was NOT sent: ${failure.message}. Use Retry email below.`);
        }
      }
      await onRefresh();
    } catch (failure) {
      setMessage(`Could not create work order: ${failure.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function retry(id) {
    setBusy(true);
    try {
      await sendEmail(id);
      setMessage('Confirmation and invoice email sent.');
    } catch (failure) {
      setMessage(`Email was NOT sent: ${failure.message}`);
    } finally {
      await onRefresh();
      setBusy(false);
    }
  }

  async function changeOrder(order, changes) {
    try {
      await updateOrder(order.id, { ...changes, updated_at: new Date().toISOString() });
      await onRefresh();
    } catch (failure) {
      setMessage(failure.message);
    }
  }

  async function saveInvoice(order) {
    setDownloading(true);
    try {
      const customer = customers.find((item) => item.id === order.customer_id);
      const pdf = await buildInvoicePdf(order, customer);
      saveBlob(pdf.blob, pdf.filename);
      setMessage('Invoice PDF downloaded.');
    } catch (failure) {
      setMessage(`Invoice download failed: ${failure.message}`);
    } finally {
      setDownloading(false);
    }
  }

  async function removeOrder(order) {
    if (!window.confirm(`Delete work order ${order.invoice_number}? This cannot be undone.`)) return;
    try {
      await deleteOrder(order.id);
      localStorage.removeItem(cooldownKey(order.id));
      setMessage('Work order deleted.');
      await onRefresh();
    } catch (failure) {
      setMessage(`Delete failed: ${failure.message}`);
    }
  }

  return (
    <>
      <div className="crm-title-row"><span>{orders.length} work orders</span><button type="button" onClick={openNew}>+ Add new workorder</button></div>
      {message && <p className="crm-notice" role="status">{message}</p>}
      <section className="crm-panel crm-feature">
        <div className="crm-panel-head"><h2>Work orders</h2></div>
        <div className="crm-records">{orders.map((order) => {
          const customer = customers.find((item) => item.id === order.customer_id);
          const wait = remainingSeconds(order.id);
          const noEmail = !customer?.email;
          return <div className="crm-record" key={order.id}>
            <div><strong>{order.service}</strong><small>{customer?.name || 'Customer'} · {order.invoice_number} · SGD {Number(order.amount).toFixed(2)} · {noEmail ? 'No email' : order.email_status}</small>
              {noEmail && customer?.phone && <small>WhatsApp: <a href={whatsappUrl(customer.phone)} target="_blank" rel="noreferrer" style={{ color: '#136c63', fontWeight: 700 }}>{customer.phone}</a></small>}
            </div>
            <select aria-label={`Status for ${order.service}`} value={order.status} onChange={(event) => changeOrder(order, { status: event.target.value })}>{['Scheduled', 'In progress', 'Completed', 'Cancelled'].map((item) => <option key={item}>{item}</option>)}</select>
            <select aria-label={`Assign worker for ${order.service}`} value={order.employee_id || ''} onChange={(event) => changeOrder(order, { employee_id: event.target.value || null })}><option value="">Unassigned</option>{employees.filter((item) => item.active || item.id === order.employee_id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
            <label style={{ display: 'grid', gap: 4, fontWeight: 700, fontSize: 12 }}>Staff pay (SGD)
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, border: '2px solid #24bf1c', borderRadius: 8, background: '#fff', padding: '4px 8px' }}>
                <b style={{ color: '#136c63', fontSize: 13 }}>SGD</b>
                <input style={{ border: 'none', outline: 'none', width: 90, fontSize: 15, fontWeight: 700, color: '#0f2a30', background: '#fff' }} aria-label={`Staff pay for ${order.service}`} type="number" min="0" step="0.01" defaultValue={order.staff_pay ?? 0} key={`${order.id}-${order.staff_pay}`} onBlur={(event) => { if (Number(event.target.value) !== Number(order.staff_pay || 0)) changeOrder(order, { staff_pay: Number(event.target.value) }); }} />
              </span>
            </label>
            <div className="crm-record-actions">
              <button type="button" onClick={() => openEdit(order)}>Edit</button>
              <button type="button" onClick={() => setSelected(order)}>Invoice</button>
              <button type="button" disabled={downloading} onClick={() => saveInvoice(order)}>{downloading ? 'Preparing…' : 'Download PDF'}</button>
              {noEmail
                ? (customer?.phone && <a className="crm-wa-btn" href={whatsappUrl(customer.phone)} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', padding: '0 14px', minHeight: 40, borderRadius: 8, background: '#25d366', color: '#fff', fontWeight: 700, textDecoration: 'none' }}>WhatsApp {customer.phone}</a>)
                : <button type="button" disabled={busy || wait > 0} onClick={() => retry(order.id)}>{wait > 0 ? `Resend in ${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, '0')}` : 'Retry email'}</button>}
              <button type="button" style={{ color: '#b42318' }} onClick={() => removeOrder(order)}>Delete</button>
            </div>
          </div>;
        })}</div>
      </section>
      {open && <div className="crm-overlay" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}><section className="crm-editor" role="dialog" aria-modal="true" aria-labelledby="workorder-title"><div className="crm-editor-top"><div><span className="crm-kicker">OPERATIONS</span><h2 id="workorder-title">{editing ? 'Edit work order' : 'New work order'}</h2><p>{editing ? `Adjust details for ${editing.invoice_number}. No email is sent when saving.` : 'Select a customer to generate their invoice and send confirmation.'}</p></div><button type="button" className="crm-close" onClick={() => setOpen(false)} aria-label="Close">×</button></div>
        <form className="crm-feature-form" onSubmit={submit}>
          <label>Customer *<select required value={customerId} onChange={(event) => setCustomerId(event.target.value)}><option value="">Select customer</option>{customers.map((item) => <option key={item.id} value={item.id}>{item.name} — {item.email || 'No email'}</option>)}</select></label>
          <label>Service *<input required value={service} onChange={(event) => setService(event.target.value)} /></label>
          <label>Scheduled start<input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} /></label>
          <label>Work minutes<input type="number" min="1" value={duration} onChange={(event) => setDuration(event.target.value)} required /></label>
          <label>Labour rate (SGD) *<input type="number" min="0" step="0.01" required value={rate} onChange={(event) => setRate(event.target.value)} /></label>
          <label>Transport (SGD)<input type="number" min="0" step="0.01" value={transport} onChange={(event) => setTransport(event.target.value)} /></label>
          <label className="crm-wide">Description<textarea value={description} onChange={(event) => setDescription(event.target.value)} /></label>
          <button disabled={busy || !customers.length} type="submit">{busy ? 'Saving…' : editing ? 'Save changes' : 'Create order & send invoice'}</button>
          {message && <p className="crm-error" role="alert">{message}</p>}
        </form></section></div>}
      {selected && <div className="crm-overlay crm-invoice-overlay" onMouseDown={(event) => event.target === event.currentTarget && setSelected(null)}><div className="crm-invoice-view"><div className="crm-invoice-tools"><button type="button" onClick={() => setSelected(null)}>Close</button><button type="button" onClick={() => saveInvoice(selected)} disabled={downloading}>{downloading ? 'Preparing…' : 'Download PDF'}</button><button type="button" onClick={() => window.print()}>Print / Save PDF</button></div><Invoice order={selected} customer={customers.find((item) => item.id === selected.customer_id)} /></div></div>}
    </>
  );
}